import { buildChartTooltip } from './chartTooltip'

const DEFAULT_TITLE = 'Gráfico de barras'
const DEFAULT_BACKGROUND_COLOR = '#FFFFFF'
const DEFAULT_TITLE_COLOR = '#002F87'
const DEFAULT_TEXT_COLOR = '#262626'
const DEFAULT_GRID_COLOR = '#E3E3E2'
const DEFAULT_LEGEND_ITEM_GAP = 10
const DEFAULT_LEGEND_TEXT_HORIZONTAL_PADDING = 5
const DEFAULT_SHORT_PERIOD_GROUP_GAP = '14%'
const DEFAULT_SHORT_PERIOD_BAR_MAX_WIDTH = 92
const DEFAULT_LONG_PERIOD_GROUP_GAP = '12%'
const DEFAULT_BAR_GAP = '0%'
const DEFAULT_LONG_PERIOD_BAR_MAX_WIDTH = 42
const DEFAULT_AXIS_LABEL_COLOR = '#262626'
const DEFAULT_X_AXIS_LABEL_COLOR = '#3C3C3B'

const getBarCategoryGap = (categoryCount) =>
  categoryCount <= 6 ? DEFAULT_SHORT_PERIOD_GROUP_GAP : DEFAULT_LONG_PERIOD_GROUP_GAP

const getBarMaxWidth = (categoryCount) =>
  categoryCount <= 6 ? DEFAULT_SHORT_PERIOD_BAR_MAX_WIDTH : DEFAULT_LONG_PERIOD_BAR_MAX_WIDTH

const getBarMinWidth = (seriesCount) => {
  if (seriesCount >= 5) {
    return 8
  }

  if (seriesCount === 4) {
    return 10
  }

  return 12
}

const getResponsiveBarLayout = (categoryCount, seriesCount, explicitBarWidth) => {
  const barCategoryGap = getBarCategoryGap(categoryCount)

  if (explicitBarWidth) {
    return {
      barWidth: explicitBarWidth,
      barGap: DEFAULT_BAR_GAP,
      barCategoryGap,
    }
  }

  return {
    barMinWidth: getBarMinWidth(seriesCount),
    barMaxWidth: getBarMaxWidth(categoryCount),
    barGap: DEFAULT_BAR_GAP,
    barCategoryGap,
  }
}

const getAxisStep = (maxValue) => {
  if (maxValue <= 0) return 4

  const roughStep = maxValue / 5
  const magnitude = 10 ** Math.floor(Math.log10(roughStep))
  const normalized = roughStep / magnitude

  if (normalized <= 1) return magnitude
  if (normalized <= 2) return magnitude * 2
  if (normalized <= 4) return magnitude * 4
  if (normalized <= 5) return magnitude * 5

  return magnitude * 10
}

const getAxisConfig = (maxValue) => {
  const interval = getAxisStep(maxValue)
  let max = Math.ceil(maxValue / interval) * interval

  if (max === 0) {
    return { max: interval, interval }
  }

  if (max - maxValue < interval * 0.6) {
    max += interval
  }

  return { max, interval }
}

export const buildColumnChartOption = ({
  categories,
  series,
  title = DEFAULT_TITLE,
  backgroundColor = DEFAULT_BACKGROUND_COLOR,
  barWidth,
}) => {
  const values = series.flatMap((item) => item.data)
  const maxValue = values.length > 0 ? Math.max(...values) : 0
  const { max, interval } = getAxisConfig(maxValue)
  const barLayout = getResponsiveBarLayout(categories.length, series.length, barWidth)

  return {
    backgroundColor,
    animation: false,
    title: {
      text: title,
      left: -4,
      top: 20,
      textStyle: {
        color: DEFAULT_TITLE_COLOR,
        fontFamily: 'Encode Sans',
        fontSize: 16,
        fontWeight: 600,
        lineHeight: 16,
        letterSpacing: 0.15,
      },
    },
    tooltip: buildChartTooltip({
      getContent: (params) => ({
        title: params.name ?? '',
        rows: [
          {
            color: params.color,
            text: `${params.seriesName}: ${params.value}`,
          },
        ],
      }),
    }),
    grid: {
      top: 56,
      right: 16,
      bottom: 72,
      left: 24,
      containLabel: true,
    },
    legend: {
      show: true,
      bottom: 0,
      left: 'center',
      itemWidth: 12,
      itemHeight: 12,
      itemGap: DEFAULT_LEGEND_ITEM_GAP,
      icon: 'rect',
      textStyle: {
        color: DEFAULT_TEXT_COLOR,
        fontFamily: 'Encode Sans',
        fontSize: 14,
        fontWeight: 600,
        lineHeight: 17.5,
        letterSpacing: 0.25,
        padding: [0, DEFAULT_LEGEND_TEXT_HORIZONTAL_PADDING, 0, DEFAULT_LEGEND_TEXT_HORIZONTAL_PADDING],
      },
    },
    xAxis: {
      type: 'category',
      data: categories,
      axisTick: {
        show: false,
      },
      axisLine: {
        lineStyle: {
          color: DEFAULT_GRID_COLOR,
          width: 1,
        },
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: DEFAULT_GRID_COLOR,
          type: [2, 2],
          width: 1,
        },
      },
      axisLabel: {
        color: DEFAULT_X_AXIS_LABEL_COLOR,
        fontFamily: 'Encode Sans',
        fontSize: 12,
        fontWeight: 500,
        lineHeight: 15,
        margin: 12,
      },
    },
    yAxis: {
      type: 'value',
      min: 0,
      max,
      interval,
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: DEFAULT_GRID_COLOR,
          type: [2, 2],
          width: 1,
        },
      },
      axisLabel: {
        color: DEFAULT_AXIS_LABEL_COLOR,
        fontFamily: 'Encode Sans',
        fontSize: 14,
        fontWeight: 500,
        lineHeight: 17.5,
        letterSpacing: 0.25,
      },
    },
    series: series.map((item) => ({
      name: item.name,
      type: 'bar',
      data: item.data,
      ...barLayout,
      itemStyle: {
        color: item.color,
        borderRadius: 0,
      },
      label: {
        show: true,
        position: 'top',
        distance: 4,
        color: DEFAULT_TEXT_COLOR,
        fontFamily: 'Encode Sans',
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 15,
        letterSpacing: 0.25,
        formatter: ({ value }) => String(value),
      },
      emphasis: {
        focus: 'none',
        disabled: true,
      },
      blur: {
        itemStyle: {
          opacity: 1,
        },
      },
    })),
  }
}

export default buildColumnChartOption
